import React, { useRef } from 'react'
import { KeyboardArrowDown, SortByAlpha } from '@mui/icons-material'
import { Grid, IconButton } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'

import { TableFilter } from '../../../../types'
import Text from '../../../Text'

const useStyles = makeStyles(
  theme => ({
    nowrap: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    secondaryText: {
      fontWeight: 400,
      fontSize: '1.2rem',
    },
    filter: {
      backgroundColor: theme.colors.tableBackground,
      border: theme.constants.tableBorder,
      cursor: 'pointer',
      maxWidth: 'fit-content',
      marginRight: theme.spacing(1),
    },
    filterActive: {
      backgroundColor: theme.colors.tabSelected,
    },
    filterText: {
      padding: theme.spacing(0.5, 1),
      borderRight: theme.constants.tableBorder,
    },
    filterTextActive: {
      color: theme.colors.profileText,
    },
    icon: {
      padding: theme.spacing(0.5),
      verticalAlign: 'middle',
    },
    iconActive: {
      color: theme.colors.profileText,
    },
  }),
  { name: 'FilterLabel' },
)

export interface FilterLabelProps {
  readonly FilterComponent?: React.JSXElementConstructor<any>
  readonly SortingIcon?: React.JSXElementConstructor<any>
  readonly className?: string
  readonly filter?: string
  readonly filterValue?: TableFilter
  readonly label?: string
  readonly onApplyFilter?: (filter: string, value: TableFilter) => void
  readonly onApplySorting?: (sorting: string, value: boolean) => void
  readonly onFilterClick: (filter: string) => void
  readonly onFilterClose?: () => void
  readonly open: boolean
  readonly sorting?: string
  readonly sortingValue?: boolean
  readonly sublabel?: string
  readonly wrap?: string
}

const FilterLabel = ({
  className,
  label,
  sublabel,
  filter,
  sortingValue,
  sorting,
  filterValue,
  open,
  FilterComponent,
  onFilterClick,
  onFilterClose,
  onApplyFilter,
  onApplySorting,
  SortingIcon,
  wrap = 'nowrap',
}: FilterLabelProps) => {
  const classes = useStyles()

  const rootRef = useRef<HTMLDivElement>(null)

  const labelConditionalClasses = {
    [classes.nowrap]: wrap === 'nowrap',
  }

  if (!filter && !sorting) {
    return (
      <Grid
        container
        item
        className={className}
        direction="column"
        pr={1}
        width="auto"
      >
        <Text
          strong
          className={classNames(labelConditionalClasses)}
          variant="lowAccent"
        >
          {label}
        </Text>
        {sublabel && (
          <Text
            strong
            className={classNames(
              classes.secondaryText,
              labelConditionalClasses,
            )}
            variant="lowAccent"
          >
            {sublabel}
          </Text>
        )}
      </Grid>
    )
  }

  const active = filter
    ? open || filterValue?.value || filterValue?.value === ''
    : sortingValue !== undefined

  const onFilterOrSortClick = () => {
    if (filter) {
      onFilterClick(filter)
    }
    if (sorting && onApplySorting) {
      onApplySorting(sorting, !sortingValue)
    }
  }

  return (
    <>
      <Grid
        container
        item
        alignItems="center"
        className={classNames(className, classes.filter, {
          [classes.filterActive]: active,
        })}
        pr={1}
        ref={rootRef}
        wrap="nowrap"
        onClick={() => onFilterOrSortClick()}
      >
        <Text
          strong
          className={classNames(classes.filterText, {
            [classes.filterTextActive]: active,
            [classes.nowrap]: wrap === 'nowrap',
          })}
          variant="lowAccent"
        >
          {label}
        </Text>
        <IconButton
          className={classNames(classes.icon, {
            [classes.iconActive]: active,
          })}
          size="large"
        >
          {filter && <KeyboardArrowDown />}
          {sorting && (SortingIcon ? <SortingIcon /> : <SortByAlpha />)}
        </IconButton>
      </Grid>
      {FilterComponent && filter && (
        <FilterComponent
          anchorEl={rootRef.current}
          humanReadable={filterValue?.humanReadable}
          open={open}
          value={filterValue?.value}
          onChange={(value: TableFilter) => {
            if (onApplyFilter) {
              onApplyFilter(filter, value)
            }
          }}
          onClose={(event: any) => {
            if (!event?.target || !rootRef.current?.contains(event.target)) {
              if (onFilterClose) {
                onFilterClose()
              }
            }
          }}
        />
      )}
    </>
  )
}

export default FilterLabel
